import {
  ICallbackBody,
  IMerchantBody,
  IMerchants,
  IPaginate,
  IPaymentCallback,
  RootState,
} from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { CreditUnionService } from '@/services/credit-union.service';

// Merchant credentials and their callback registrations — both are KairosKash
// admin concerns reached through the credit-union proxy.
const state = {
  merchants: [] as IMerchants[],
  merchantsPagination: {
    total: 0,
    itemsPerPage: 30,
    page: 1,
    pages: 0,
  } as IPaginate,
  callbacks: [] as IPaymentCallback[],
  callbacksPagination: {
    total: 0,
    itemsPerPage: 30,
    page: 1,
    pages: 0,
  } as IPaginate,
};

type PaymentAdminState = typeof state;

const mutations: MutationTree<PaymentAdminState> = {
  UPDATE_MERCHANTS(state, payload: IMerchants[]) {
    state.merchants = payload;
  },
  UPDATE_MERCHANTS_PAGINATION(state, payload: IPaginate) {
    state.merchantsPagination = payload;
  },
  UPDATE_CALLBACKS(state, payload: IPaymentCallback[]) {
    state.callbacks = payload;
  },
  UPDATE_CALLBACKS_PAGINATION(state, payload: IPaginate) {
    state.callbacksPagination = payload;
  },
};

// KairosKash paginates as { docs, totalDocs, totalPages, page, size }; some
// endpoints return a bare array when isPaginated=false.
const readPagination = (payload: any, fallbackSize: number): IPaginate => ({
  page: payload?.page ?? 1,
  pages: payload?.totalPages ?? 0,
  total: payload?.totalDocs ?? payload?.total ?? 0,
  itemsPerPage: payload?.size ?? fallbackSize,
});

const actions: ActionTree<PaymentAdminState, RootState> = {
  async fetchMerchants({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await CreditUnionService.listAllMerchants(query);
      const payload = response$?.data?.data;
      commit('UPDATE_MERCHANTS', payload?.docs ?? payload ?? []);
      commit('UPDATE_MERCHANTS_PAGINATION', readPagination(payload, 30));
      dispatch('isPageLoading', false, { root: true });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async createMerchant({ dispatch }, payload: { body: IMerchantBody; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await CreditUnionService.createMerchant(payload.body);
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
      dispatch('snackBarMessage', 'Merchant credentials created successfully', { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchMerchants', payload.query);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async fetchCallbacks({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await CreditUnionService.listCallbacks(query);
      const payload = response$?.data?.data;
      commit('UPDATE_CALLBACKS', payload?.docs ?? payload ?? []);
      commit('UPDATE_CALLBACKS_PAGINATION', readPagination(payload, 30));
      dispatch('isPageLoading', false, { root: true });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async createCallback({ dispatch }, payload: { body: ICallbackBody; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await CreditUnionService.createCallback(payload.body);
      dispatch('isLoading', false, { root: true });
      dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
      dispatch('snackBarMessage', 'Callback registered successfully', { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('fetchCallbacks', payload.query);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
};

const getters: GetterTree<PaymentAdminState, RootState> = {
  getMerchants: state => state.merchants,
  getMerchantsPagination: state => state.merchantsPagination,
  getCallbacks: state => state.callbacks,
  getCallbacksPagination: state => state.callbacksPagination,
};

export const paymentAdmin: Module<PaymentAdminState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
